import { setTourTarget } from "@/components/onboarding/tourTargets";
import { useAuth } from "@/contexts/AuthContext";
import { MaterialIcons } from "@/ui/icons";
import { cx } from "@/ui/cx";
import {
  BottomSheetBackdrop,
  BottomSheetModal,
  BottomSheetScrollView,
  type BottomSheetBackdropProps,
} from "@/ui/bottom-sheet";
import { Modal } from "@/ui/Modal";
import { useSafeAreaInsets } from "@/ui/safe-area";
import * as Linking from "@/platform/linking";
import { BackHandler } from "@/platform/back-handler";
import { DeviceEventEmitter } from "@/platform/events";
import { Dimensions } from "@/platform/dimensions";
import { Keyboard } from "@/platform/keyboard";
import { Platform } from "@/platform/platform";
import {
  useGlobalSearchParams,
  usePathname,
} from "@/navigation";
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import {
  COR_GRUPO,
  PENDENCIAS_VAZIAS,
  abrirSuporteChat,
  acessarAdministrativo as acessarAdministrativoComum,
  caminhoDaAba,
  ehDesktop,
  executarSaida,
  linhasPendencias,
  mudarPerfil as mudarPerfilComum,
  MUDAR_PERFIL_VISIVEL,
  navegarParaAba,
  pendenciasQueBloqueiam,
  registrarPonto,
  useRegrasMenu,
  type PendenciasSync,
} from "./itensMenu";
import s from "./BottomBar.module.css";
import { T, TC, TM } from "@/utils/i18n";

/*
 * Estilo dos cards do menu (ESTILO_MENU do nativo → classes do BottomBar.module.css).
 *
 * Card branco sobre fundo cinza-frio (o sheet deixou de ser branco por isso): é o
 * contraste do card contra o fundo que faz o GRUPO existir aos olhos. Com tudo branco
 * a separação dependeria de bordas, e borda fina some em tela de celular barato sob
 * luz forte — que é a condição de uso real deste app.
 *
 * `overflow: hidden` no card não é detalhe: sem ele o realce de toque da primeira e da
 * última linha vaza por cima do canto arredondado.
 */

// Valor em português: vai no texto da mensagem para o suporte da Tirvu. A tela mostra TM(opt).
const SUPORTE_OPTIONS = [
  TC("Tirar dúvidas"),
  TC("Relatar erro/bug"),
  TC("Sugestão de melhoria"),
  TC("Outros"),
];

/**
 * WEB: o nativo recebe `BottomTabBarProps` do Tab Navigator (state, navigation,
 * descriptors, insets). No web a barra é renderizada pelo shell SEM essas props: a aba
 * ativa vem de `usePathname()` e a navegação de `router.push`. As props são aceitas
 * (opcionais) só para compatibilidade e são ignoradas.
 */
export type BottomBarProps = {
  state?: unknown;
  navigation?: unknown;
  descriptors?: unknown;
  insets?: unknown;
};

/** WEB: registra o alvo do tour só quando a barra está visível (< 1024px). */
const alvoTour = (key: string) => (node: HTMLElement | null) => {
  if (ehDesktop()) return;
  setTourTarget(key, node as any);
};

/** WEB: `onLayout`/`onContentSizeChange` → ResizeObserver no nó medido. */
function useAlturaObservada(onAltura: (h: number) => void) {
  const cbRef = useRef(onAltura);
  cbRef.current = onAltura;
  const roRef = useRef<ResizeObserver | null>(null);
  return useCallback((node: HTMLElement | null) => {
    roRef.current?.disconnect();
    roRef.current = null;
    if (!node) return;
    const medir = () => cbRef.current(node.offsetHeight);
    medir();
    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(medir);
      ro.observe(node);
      roRef.current = ro;
    }
  }, []);
}

type SuporteMensagemHandle = { getValue: () => string; clear: () => void };

// Campo "Sua mensagem" isolado e NÃO-CONTROLADO: guarda o texto num ref (sem
// state, sem value), então digitar não re-renderiza o BottomBar. Isso elimina a
// corrida entre o value controlado e o IME do Android (o "texto doido" que
// inseria caracteres do nada dentro do BottomSheet).
const SuporteMensagemInput = memo(
  forwardRef<SuporteMensagemHandle, { scrollRef: { current: any } }>(
    function SuporteMensagemInput({ scrollRef }, ref) {
      const inputRef = useRef<HTMLTextAreaElement>(null);
      const valueRef = useRef("");
      useImperativeHandle(ref, () => ({
        getValue: () => valueRef.current,
        clear: () => {
          valueRef.current = "";
          // `clear()` do RN não dispara onChangeText — aqui também não.
          if (inputRef.current) inputRef.current.value = "";
        },
      }));
      return (
        <textarea
          ref={inputRef}
          rows={4}
          defaultValue=""
          onChange={(e) => {
            valueRef.current = e.target.value;
          }}
          onFocus={() => {
            setTimeout(() => {
              const el = scrollRef.current;
              // WEB: o ref do contêiner de rolagem pode ser o próprio <div>.
              if (el?.scrollToEnd) el.scrollToEnd({ animated: true });
              else el?.scrollTo?.({ top: el.scrollHeight, behavior: "smooth" });
            }, 180);
          }}
          autoCorrect="off"
          autoComplete="off"
          autoCapitalize="sentences"
          spellCheck={false}
          inputMode="text"
          placeholder={T("Descreva brevemente o que precisa...")}
          className={s.suporteInput}
        />
      );
    },
  ),
);

/**
 * Modais de saída ("Confirmar Saída?" e "Não é possível sair").
 * WEB: exportado para o MenuLateral (desktop) reusar o MESMO desenho e fluxo.
 */
export function ModaisSaida({
  logoutModalOpen,
  onCloseLogout,
  onConfirmLogout,
  bloqueioSaidaOpen,
  onCloseBloqueio,
  pendenciasSync,
}: {
  logoutModalOpen: boolean;
  onCloseLogout: () => void;
  onConfirmLogout: () => void;
  bloqueioSaidaOpen: boolean;
  onCloseBloqueio: () => void;
  pendenciasSync: PendenciasSync;
}) {
  return (
    <>
      <Modal
        visible={logoutModalOpen}
        transparent
        animationType="fade"
        onRequestClose={onCloseLogout}
      >
        <div className={s.modalFundo}>
          <div className={s.modalCaixa} role="dialog" aria-modal="true">
            <span className={s.modalTitulo}>{T("Confirmar Saída?")}</span>
            <span className={s.modalTexto}>
              {T("Sua sessão será encerrada e precisará de internet para logar novamente.")}
            </span>
            <div className={s.modalAcoes}>
              <button
                type="button"
                onClick={onCloseLogout}
                className={s.botaoCancelar}
              >
                <span className={s.botaoCancelarTexto}>{T("Cancelar")}</span>
              </button>
              <button
                type="button"
                onClick={onConfirmLogout}
                className={s.botaoSair}
              >
                <span className={s.botaoSairTexto}>{T("Sair")}</span>
              </button>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        visible={bloqueioSaidaOpen}
        transparent
        animationType="fade"
        onRequestClose={onCloseBloqueio}
      >
        <div className={s.modalFundo}>
          <div className={s.modalCaixa} role="dialog" aria-modal="true">
            <div className={s.bloqueioIcone}>
              <MaterialIcons name="cloud-off" size={26} color="#e08a3c" />
            </div>
            <span className={s.modalTitulo}>{T("Não é possível sair")}</span>
            {pendenciasSync.total > 0 ? (
              <>
                <span className={s.modalTexto}>
                  {T("Você tem dados aguardando sincronização!")}
                </span>
                <span className={s.modalTexto}>
                  {T("Conecte-se à internet e aguarde a sincronização antes de sair. Senão eles serão perdidos:")}
                </span>
              </>
            ) : (
              <span className={s.modalTexto}>
                {T("Não deu para conferir agora se há dados aguardando sincronização neste aparelho. Para não perder nada, a saída fica bloqueada: mantenha o app aberto com internet por alguns minutos e tente de novo.")}
              </span>
            )}
            <div className={s.pendencias}>
              {linhasPendencias(pendenciasSync).map((it, idx) => (
                <div key={idx} className={s.pendencia}>
                  <MaterialIcons
                    name={it.icon as any}
                    size={18}
                    color={COR_GRUPO.ponto}
                  />
                  <span className={s.pendenciaTexto}>{it.label}</span>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={onCloseBloqueio}
              className={s.botaoEntendi}
            >
              <span className={s.botaoEntendiTexto}>{T("Entendi")}</span>
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
/**
 * WEB: publica a altura real da barra em `--altura-bottom-bar` (no :root) para os botões
 * flutuantes das telas ficarem sempre acima dela (`--fab-bottom` em styles/tokens.css).
 * Ref de callback: funciona mesmo com o `return null` condicional da barra.
 */
let observadorBarra: ResizeObserver | null = null;
function medirBarra(el: HTMLDivElement | null) {
  observadorBarra?.disconnect();
  observadorBarra = null;
  const raiz = document.documentElement;
  if (!el) {
    raiz.style.setProperty("--altura-bottom-bar", "0px");
    return;
  }
  const aplicar = () => raiz.style.setProperty("--altura-bottom-bar", `${el.offsetHeight}px`);
  aplicar();
  if (typeof ResizeObserver !== "undefined") {
    observadorBarra = new ResizeObserver(aplicar);
    observadorBarra.observe(el);
  }
}

export default function BottomBar(_props: BottomBarProps = {}) {
  const { signOut, userData } = useAuth();
  const rotaAtual = usePathname();
  const paramsRota = useGlobalSearchParams() as Record<string, unknown>;
  const [menuOpen, setMenuOpen] = useState(false);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const [bloqueioSaidaOpen, setBloqueioSaidaOpen] = useState(false);
  const [pendenciasSync, setPendenciasSync] =
    useState<PendenciasSync>(PENDENCIAS_VAZIAS);
  const [suporteModalOpen, setSuporteModalOpen] = useState(false);
  const [suporteAssunto, setSuporteAssunto] = useState("");
  const suporteInputRef = useRef<SuporteMensagemHandle>(null);

  // WEB: flags do /iniciar, grupos do menu e badge de documentos vivem em ./itensMenu
  // (compartilhados com o MenuLateral do desktop) — mesmas regras do nativo.
  const {
    items,
    menuGrupos,
    inativo,
    temAcessoAdm,
    portalHash,
    permitePonto,
  } = useRegrasMenu();

  const insets = useSafeAreaInsets();
  const sheetRef = useRef<BottomSheetModal>(null);
  const suporteSheetRef = useRef<BottomSheetModal>(null);
  const suporteScrollRef = useRef<any>(null);
  const pendingLogoutModalRef = useRef(false);
  const pendingBloqueioRef = useRef(false);
  const [contentH, setContentH] = useState(360);
  const [suporteContentH, setSuporteContentH] = useState(400);
  // Altura do teclado — levanta o sheet de suporte para o textarea não ser coberto.
  const [kbHeight, setKbHeight] = useState(0);
  /**
   * Teto da altura do MENU: 99,5% da tela.
   *
   * Era 90%, e com muitos itens visíveis o "Sair" (que é o ÚLTIMO) ficava fora da
   * área do sheet — enquanto sobrava faixa vazia no topo. O `Math.min` continua
   * mandando: o sheet só cresce até o tamanho do CONTEÚDO; o teto existe para nunca
   * passar da tela.
   */
  const ALTURA_MAX_MENU = 0.995;
  const alturaJanela = Dimensions.get("window").height;
  const alturaMenu = useMemo(
    () =>
      Math.min(
        contentH + insets.bottom,
        Math.floor(alturaJanela * ALTURA_MAX_MENU),
      ),
    [contentH, insets.bottom, alturaJanela],
  );
  const snapPoints = useMemo(() => [alturaMenu], [alturaMenu]);
  const suporteSnapPoints = useMemo(
    () => [
      Math.min(
        suporteContentH + insets.bottom,
        Math.floor(Dimensions.get("window").height * 0.9),
      ),
    ],
    [suporteContentH, insets.bottom],
  );

  // Mede o CONTEÚDO e soma de volta os paddings do container, para o `contentH`
  // continuar significando a mesma coisa que significava com o onLayout no view
  // padded — a conta do snapPoint não muda.
  const medirMenu = useAlturaObservada((h) =>
    setContentH(h + 8 + Math.max(insets.bottom, 20)),
  );
  // WEB: `onContentSizeChange` do ScrollView → altura do contêiner de conteúdo.
  const medirSuporte = useAlturaObservada((h) => setSuporteContentH(h));

  // Rastreia a altura do teclado enquanto o sheet de suporte está aberto.
  useEffect(() => {
    if (!suporteModalOpen) {
      setKbHeight(0);
      return;
    }
    const showEvent =
      Platform.OS === "ios" ? "keyboardWillShow" : "keyboardDidShow";
    const hideEvent =
      Platform.OS === "ios" ? "keyboardWillHide" : "keyboardDidHide";
    const showSub = Keyboard.addListener(showEvent as any, (e: any) => {
      const h = Number(e?.endCoordinates?.height ?? 0);
      setKbHeight(Number.isFinite(h) ? h : 0);
    });
    const hideSub = Keyboard.addListener(hideEvent as any, () =>
      setKbHeight(0),
    );
    return () => {
      showSub.remove();
      hideSub.remove();
    };
  }, [suporteModalOpen]);

  // WEB: rota ativa = pathname; params da rota = query string (Ronda faz
  // `router.setParams({ scanning })`).
  const isRondaScanning =
    rotaAtual === caminhoDaAba("ronda") &&
    String(paramsRota?.scanning ?? "") === "true";
  const hidden = isRondaScanning;

  const closeMenu = () => {
    sheetRef.current?.dismiss();
    setMenuOpen(false);
  };

  const closeLogoutModal = () => {
    setLogoutModalOpen(false);
  };

  const closeSuporteSheet = () => {
    suporteSheetRef.current?.dismiss();
    setSuporteModalOpen(false);
    suporteInputRef.current?.clear();
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const openSuporteSheet = () => {
    setSuporteModalOpen(true);
    suporteSheetRef.current?.present();
  };

  const openLogoutModal = async () => {
    // BLOQUEIA a saída se houver dados pendentes de sincronização (ou se não deu para
    // conferir) — o logout faz AsyncStorage.clear() e apagaria o que ainda não subiu ao
    // servidor. Mostra o modal de bloqueio (listando o que falta) em vez do de confirmação.
    const bloqueio = await pendenciasQueBloqueiam();
    if (bloqueio) {
      setPendenciasSync(bloqueio);
      pendingBloqueioRef.current = true;
      closeMenu();
      return;
    }
    pendingLogoutModalRef.current = true;
    closeMenu();
  };

  const acessarAdministrativo = async () => {
    sheetRef.current?.dismiss();
    setMenuOpen(false);
    // Mesmo fluxo comprovado do EnvironmentSelect: persiste ambiente+hash e usa replace
    // (o PortalWeb lê do storage, de forma confiável). fromApp=1 mostra o botão de voltar,
    // que restaura o ambiente 'colaborador' ao sair (sem sequestrar o startup).
    await acessarAdministrativoComum(portalHash);
  };

  /**
   * MUDAR PERFIL → tela de escolha de acesso (Colaborador · Administrador ·
   * Cliente · Prestador), que é a `EnvironmentSelect`.
   *
   * ⚠️ NÃO desloga: trocar de perfil não é sair. A sessão do colaborador continua
   * viva (inclusive a fila offline dele) e o usuário volta para ela sem relogar.
   *
   * Antes daqui saía o "Possui acesso administrativo?", que ia DIRETO para o
   * WebView admin quando havia TirvuID salvo. Isso deixou de fazer sentido: o
   * botão agora é a porta para os QUATRO ambientes, e quem escolhe é o usuário —
   * atalhar para um deles tiraria dele a escolha que o botão promete.
   */
  const mudarPerfil = () => {
    sheetRef.current?.dismiss();
    setMenuOpen(false);
    mudarPerfilComum();
  };

  /**
   * Abre o bloqueio DEPOIS que o modal de confirmação terminou de fechar: apresentar um modal
   * enquanto outro sai de cena é a combinação que derruba o app no iPhone.
   */
  const bloquearDepoisDaConfirmacao = (pend: PendenciasSync) => {
    setPendenciasSync(pend);
    setTimeout(() => setBloqueioSaidaOpen(true), 450);
  };

  const confirmLogout = async () => {
    setLogoutModalOpen(false);
    // Confere de novo, limpa preservando o que precisa sobreviver, signOut e
    // replace("/environment-select") — ver executarSaida em ./itensMenu.
    await executarSaida(signOut, bloquearDepoisDaConfirmacao);
  };

  const handleEnviarSuporte = () => {
    if (!suporteAssunto) return;

    const nome = userData?.nome || "Não identificado";
    const cpf = userData?.cpf || "Não informado";
    const empresa = userData?.posto || "Não vinculada";

    const mensagem =
      suporteInputRef.current?.getValue()?.trim() || "Sem mensagem adicional";

    const texto = `*SUPORTE TIRVU+*

*Assunto:* ${suporteAssunto}
*Mensagem:* ${mensagem}

*DADOS DO USUÁRIO:*
*Nome:* ${nome}
*CPF:* ${cpf}
*Empresa:* ${empresa}`;

    const url = `https://api.whatsapp.com/send?phone=556135362934&text=${encodeURIComponent(texto)}`;
    Linking.openURL(url);
    closeSuporteSheet();
    setSuporteAssunto("");
    suporteInputRef.current?.clear();
  };

  const expandSheet = () => {
    setMenuOpen(true);
    sheetRef.current?.present();
  };

  // WEB: `Gesture.Pan().onEnd(e => !menuOpen && e.translationY < -40 → abre)` →
  // Pointer Events no botão Menu (arrastar para cima abre o menu).
  const panInicioY = useRef<number | null>(null);
  const abriuPorArrasto = useRef(false);
  const panDown = (e: ReactPointerEvent<HTMLButtonElement>) => {
    panInicioY.current = e.clientY;
    abriuPorArrasto.current = false;
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // sem captura: o pointerup ainda chega se o dedo terminar sobre o botão
    }
  };
  const panUp = (e: ReactPointerEvent<HTMLButtonElement>) => {
    const y0 = panInicioY.current;
    panInicioY.current = null;
    if (y0 == null) return;
    const translationY = e.clientY - y0;
    if (!menuOpen && translationY < -40) {
      abriuPorArrasto.current = true;
      expandSheet();
    }
  };

  useEffect(() => {
    const sub = BackHandler.addEventListener("hardwareBackPress", () => {
      if (logoutModalOpen) {
        closeLogoutModal();
        return true;
      }
      if (suporteModalOpen) {
        closeSuporteSheet();
        return true;
      }
      if (menuOpen) {
        closeMenu();
        return true;
      }
      return false;
    });
    return () => sub.remove();
  }, [logoutModalOpen, suporteModalOpen, menuOpen]);

  // Atalho "Suporte" da Home abre o mesmo sheet de Suporte Técnico desta barra.
  useEffect(() => {
    const sub = DeviceEventEmitter.addListener("open-suporte-tecnico", () => {
      setSuporteModalOpen(true);
      suporteSheetRef.current?.present();
    });
    return () => sub.remove();
  }, []);

  if (hidden) {
    return null;
  }

  return (
    <div className={s.raiz} ref={medirBarra}>
      <div className={s.barra}>
        <div className={s.linhaItens}>
          {items.map((item, i) => {
            const focused = rotaAtual === caminhoDaAba(item.key);
            const color = focused ? COR_GRUPO.ponto : "#294956";
            const classeItem = cx(
              s.item,
              i === 1 && s.itemAntesDoVao,
              i === 2 && s.itemDepoisDoVao,
            );
            return item.key === "menu" ? (
              <button
                key={item.key}
                type="button"
                aria-label={item.label}
                onPointerDown={panDown}
                onPointerUp={panUp}
                onPointerCancel={() => {
                  panInicioY.current = null;
                }}
                onClick={() => {
                  if (abriuPorArrasto.current) {
                    abriuPorArrasto.current = false;
                    return;
                  }
                  setMenuOpen(true);
                  sheetRef.current?.present();
                }}
                className={cx(classeItem, s.itemMenu)}
              >
                {/* Ref do tour numa View INTERNA (no nativo o GestureDetector injeta
                    a própria ref no filho direto). */}
                <div ref={alvoTour(`bottombar:${item.key}`)} className={s.itemInterno}>
                  <div className={s.itemCaixa}>
                    <MaterialIcons name={item.icon} color={color} size={24} />
                  </div>
                  <span className={s.itemRotulo}>{item.label}</span>
                </div>
              </button>
            ) : (
              <button
                key={item.key}
                type="button"
                ref={alvoTour(`bottombar:${item.key}`)}
                onClick={() => {
                  if (item.key === "suporte") {
                    // Antes abria o bottomsheet de suporte técnico (desativado):
                    // openSuporteSheet();
                    // Agora abre a página do chat de suporte.
                    abrirSuporteChat();
                  } else {
                    navegarParaAba(item.key, rotaAtual);
                  }
                }}
                className={classeItem}
              >
                <div className={s.itemCaixa}>
                  <MaterialIcons name={item.icon} color={color} size={24} />
                  {item.badge ? <div className={s.badge} /> : null}
                </div>
                <span className={s.itemRotulo}>{item.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <button
        type="button"
        aria-label={T("Registrar ponto")}
        ref={alvoTour("bottombar:camera")}
        onClick={() => registrarPonto(inativo, permitePonto, rotaAtual)}
        // No nativo a ordem de pintura do botão central sobre o fundo da barra dependia
        // da elevation (Android 9); aqui o z-index do CSS garante que ele fique por cima.
        className={s.botaoCamera}
      >
        <div
          className={cx(
            s.botaoCameraCirculo,
            (inativo || !permitePonto) && s.botaoCameraDesativado,
          )}
        >
          <MaterialIcons name="photo-camera" color="#fff" size={28} />
        </div>
      </button>

      <BottomSheetModal
        ref={sheetRef}
        snapPoints={snapPoints}
        // Impede o sheet de subir POR BAIXO do status bar/notch. É isto — e não
        // padding no conteúdo — que resolve o topo: com padding, o vão aparecia
        // DENTRO do menu (entre a borda e o título) e ainda inflava o `contentH`
        // medido no onLayout. Com topInset, o sheet para onde a área útil começa e
        // o conteúdo continua colado no topo dele.
        topInset={insets.top}
        backdropComponent={(props: BottomSheetBackdropProps) => (
          <BottomSheetBackdrop
            {...props}
            appearsOnIndex={0}
            disappearsOnIndex={-1}
            pressBehavior="close"
          />
        )}
        onDismiss={() => {
          setMenuOpen(false);

          if (pendingLogoutModalRef.current) {
            pendingLogoutModalRef.current = false;
            setLogoutModalOpen(true);
          }
          if (pendingBloqueioRef.current) {
            pendingBloqueioRef.current = false;
            setBloqueioSaidaOpen(true);
          }
        }}
        handleIndicatorStyle={{ backgroundColor: "#c2d0d8", width: 44 }}
        // Fundo CINZA-FRIO, não branco: é o contraste contra ele que faz cada card de
        // grupo existir. Com sheet branco + card branco a separação dependeria só da
        // borda de 1px — que some em tela de celular barato sob sol.
        backgroundStyle={{
          backgroundColor: "#f3f7f9",
          borderTopLeftRadius: 28,
          borderTopRightRadius: 28,
        }}
      >
        {/*
          ⚠️ ROLÁVEL, e isto é obrigatório depois do agrupamento.
          Era um `BottomSheetView` (sem rolagem) porque a lista chapada cabia na tela.
          Cabeçalho + card por grupo somam altura, e num aparelho baixo o conteúdo passa
          do teto de 99,5%: sem rolagem o "Sair" — que é o último — voltaria a ficar fora
          do sheet, exatamente o defeito que o teto de altura foi criado para resolver.
          O `snapPoints` continua mandando (o sheet só cresce até o conteúdo); a rolagem
          existe para quando o conteúdo é maior que a tela.
        */}
        <BottomSheetScrollView showsVerticalScrollIndicator={false}>
          {/* WEB: `contentContainerStyle` → este contêiner com os mesmos paddings. */}
          <div
            className={s.menuConteudo}
            style={{ paddingBottom: Math.max(insets.bottom, 20) }}
          >
            <div ref={medirMenu}>
              <span className={s.menuTitulo}>{T("Menu")}</span>

              {temAcessoAdm && !inativo && (
                <button
                  type="button"
                  onClick={acessarAdministrativo}
                  className={s.adm}
                >
                  <div className={s.admPastilha}>
                    <MaterialIcons
                      name="admin-panel-settings"
                      size={24}
                      color="#fff"
                    />
                  </div>
                  <div className={s.admTextos}>
                    <span className={s.admTitulo}>{T("Acessar Administrativo")}</span>
                    <span className={s.admSub}>
                      {T("Entrar no painel administrativo da empresa")}
                    </span>
                  </div>
                  <MaterialIcons name="arrow-forward" size={20} color="#fff" />
                </button>
              )}

              {menuGrupos.map((grupo) => (
                <div key={grupo.id} className={s.grupo}>
                  <span className={s.tituloGrupo}>{grupo.titulo}</span>
                  <div className={s.card}>
                    {grupo.itens.map((it, idx) => (
                      <div key={it.route}>
                        <button
                          type="button"
                          onClick={() => {
                            sheetRef.current?.dismiss();
                            setMenuOpen(false);
                            navegarParaAba(it.route, rotaAtual);
                          }}
                          // Duas linhas de texto não cabem nos 54 px: a linha cresce
                          // com o conteúdo e ganha respiro em cima e embaixo.
                          className={cx(s.linha, it.sub && s.linhaComSub)}
                        >
                          {/* Fundo da pastilha = a cor do grupo a 10% (`1a` é o alfa em
                            hexa de 8 dígitos). Mesmo matiz do ícone, então a pastilha
                            nunca briga com ele. */}
                          <div
                            className={s.pastilha}
                            style={{ backgroundColor: `${grupo.cor}1a` }}
                          >
                            <MaterialIcons
                              name={it.icon as any}
                              size={20}
                              color={grupo.cor}
                            />
                          </div>
                          {it.sub ? (
                            // Coluna própria: o `rotulo` solto tem `flex: 1` (para ocupar
                            // a LINHA); dentro de uma coluna sem altura ele colapsa a 0 e
                            // o subtítulo pinta por cima do título. O flex fica na coluna
                            // e o título usa a variante sem flex.
                            <div className={s.colunaSub}>
                              <span className={s.rotuloComSub}>{it.label}</span>
                              <span className={s.subRotulo}>{it.sub}</span>
                            </div>
                          ) : (
                            <span className={s.rotulo}>{it.label}</span>
                          )}
                          <MaterialIcons
                            name="chevron-right"
                            size={20}
                            color="#b7c6ce"
                          />
                        </button>
                        {idx < grupo.itens.length - 1 ? (
                          <div className={s.divisor} />
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {/* ── Conta ────────────────────────────────────────────────────────────
                Fecha o menu, depois dos assuntos: é para onde se vai quando o que se
                queria não era uma funcionalidade. "Mudar Perfil de Acesso" continua
                SEMPRE visível — é a porta para os quatro ambientes e não depende de o
                colaborador ter acesso ao painel administrativo. */}
              <div className={s.grupo}>
                <span className={s.tituloGrupo}>{T("Conta")}</span>
                <div className={s.card}>
                  <button
                    type="button"
                    onClick={() => {
                      sheetRef.current?.dismiss();
                      setMenuOpen(false);
                      navegarParaAba("menu", rotaAtual);
                    }}
                    className={s.linha}
                  >
                    <div
                      className={s.pastilha}
                      style={{ backgroundColor: `${COR_GRUPO.conta}1a` }}
                    >
                      <MaterialIcons
                        name="account-circle"
                        size={20}
                        color={COR_GRUPO.conta}
                      />
                    </div>
                    <span className={s.rotulo}>{T("Meus Dados")}</span>
                    <MaterialIcons
                      name="chevron-right"
                      size={20}
                      color="#b7c6ce"
                    />
                  </button>

                  <div className={s.divisor} />

                  {/* Biometria — saiu de dentro do "Meus Dados", que é um formulário
                    de telefone, PIX e banco com um botão "Salvar" no rodapé. A face
                    não se salva por ali (a captura grava sozinha) e o
                    compartilhamento com os coletores é decisão de privacidade, que
                    precisava de espaço para ser explicada antes de ser tomada. */}
                  <button
                    type="button"
                    onClick={() => {
                      sheetRef.current?.dismiss();
                      setMenuOpen(false);
                      navegarParaAba("biometria", rotaAtual);
                    }}
                    className={s.linha}
                  >
                    <div
                      className={s.pastilha}
                      style={{ backgroundColor: `${COR_GRUPO.conta}1a` }}
                    >
                      <MaterialIcons
                        name="face"
                        size={20}
                        color={COR_GRUPO.conta}
                      />
                    </div>
                    <span className={s.rotulo}>{T("Biometria")}</span>
                    <MaterialIcons
                      name="chevron-right"
                      size={20}
                      color="#b7c6ce"
                    />
                  </button>

                  {MUDAR_PERFIL_VISIVEL && <div className={s.divisor} />}

                  {MUDAR_PERFIL_VISIVEL && (
                  <button type="button" onClick={mudarPerfil} className={s.linha}>
                    {/* Pastilha SÓLIDA, e não a 10% como as outras: é o único degrau de
                      destaque que este item recebe. Era um banner PRIMARY chapado de
                      largura inteira, que no meio da lista lia como propaganda — o
                      pedido do produto era "destaque no visual, sem virar um banner".
                      Já foi tentado tirá-lo do grupo, em card próprio com borda
                      PRIMARY: ficou pior. Dentro da Conta é o lugar dele. */}
                    <div className={cx(s.pastilha, s.pastilhaSolida)}>
                      <MaterialIcons name="swap-horiz" size={20} color="#fff" />
                    </div>
                    <span className={s.rotulo}>{T("Mudar Perfil de Acesso")}</span>
                    <MaterialIcons
                      name="chevron-right"
                      size={20}
                      color="#b7c6ce"
                    />
                  </button>
                  )}
                </div>
              </div>

              {/* Sair é LABEL, não card: fica sobre o fundo do sheet, sem superfície
                própria, sem pastilha e sem seta.
                Ação destrutiva não divide vizinhança com navegação — encostar por
                engano custa a sessão, e num app offline-first isso pode custar
                pendência não sincronizada junto. Tirar a aparência de item de lista
                é o que impede o toque por reflexo: nada aqui convida ao caminho.

                ⚠️ `hitSlop` em vez de altura de card. Sem superfície, o alvo de toque
                encolheu para a altura do texto; o hitSlop (WEB: pseudo-elemento
                ::before) devolve a área tocável sem devolver a moldura. */}
              <button type="button" onClick={openLogoutModal} className={s.sair}>
                <MaterialIcons name="logout" size={18} color="#d94446" />
                {/* Sublinhado: sem card e sem seta, é o que devolve o sinal de que
                  isto é tocável. */}
                <span className={s.sairTexto}>{T("Sair do Aplicativo")}</span>
              </button>
            </div>
          </div>
        </BottomSheetScrollView>
      </BottomSheetModal>

      <ModaisSaida
        logoutModalOpen={logoutModalOpen}
        onCloseLogout={closeLogoutModal}
        onConfirmLogout={confirmLogout}
        bloqueioSaidaOpen={bloqueioSaidaOpen}
        onCloseBloqueio={() => setBloqueioSaidaOpen(false)}
        pendenciasSync={pendenciasSync}
      />

      <BottomSheetModal
        ref={suporteSheetRef}
        snapPoints={suporteSnapPoints}
        enableDynamicSizing={false}
        enablePanDownToClose
        backdropComponent={(props: BottomSheetBackdropProps) => (
          <BottomSheetBackdrop
            {...props}
            appearsOnIndex={0}
            disappearsOnIndex={-1}
            pressBehavior="close"
            opacity={0.5}
          />
        )}
        onDismiss={() => setSuporteModalOpen(false)}
        handleIndicatorStyle={{ backgroundColor: "#1a1a1a" }}
        backgroundStyle={{
          backgroundColor: "white",
          borderTopLeftRadius: 28,
          borderTopRightRadius: 28,
        }}
        bottomInset={insets.bottom}
        // WEB: keyboardBehavior/keyboardBlurBehavior/android_keyboardInputMode são do
        // teclado nativo; no browser o viewport `interactive-widget=resizes-content`
        // já redimensiona a página com o teclado.
      >
        <div className={s.suporteRaiz}>
          <BottomSheetScrollView ref={suporteScrollRef}>
            <div
              ref={medirSuporte}
              className={s.suporteConteudo}
              style={{
                paddingBottom: 96 + Math.max(insets.bottom, 16) + kbHeight,
              }}
            >
              <div className={s.suporteCabecalho}>
                <span className={s.suporteTitulo}>{T("Suporte Técnico")}</span>
                <button
                  type="button"
                  aria-label={T("Fechar")}
                  onClick={closeSuporteSheet}
                >
                  <MaterialIcons name="close" size={24} color={COR_GRUPO.ponto} />
                </button>
              </div>

              <span className={s.suporteRotulo}>{T("SELECIONE O ASSUNTO:")}</span>
              <div className={s.suporteOpcoes}>
                {SUPORTE_OPTIONS.map((opt) => {
                  const isSelected = suporteAssunto === opt;
                  return (
                    <button
                      type="button"
                      key={opt}
                      onClick={() => setSuporteAssunto(opt)}
                      className={cx(
                        s.suporteOpcao,
                        isSelected && s.suporteOpcaoSelecionada,
                      )}
                    >
                      <span
                        className={cx(
                          s.suporteOpcaoTexto,
                          isSelected && s.suporteOpcaoTextoSelecionado,
                        )}
                      >
                        {TM(opt)}
                      </span>
                    </button>
                  );
                })}
              </div>

              <span className={s.suporteRotulo}>{T("SUA MENSAGEM:")}</span>
              <SuporteMensagemInput
                ref={suporteInputRef}
                scrollRef={suporteScrollRef}
              />
            </div>
          </BottomSheetScrollView>

          {/* Botão Enviar fixo — acompanha o teclado (sobe junto). */}
          <div className={s.suporteRodape} style={{ bottom: kbHeight }}>
            <button
              type="button"
              onClick={handleEnviarSuporte}
              disabled={!suporteAssunto}
              className={cx(
                s.suporteEnviar,
                !suporteAssunto && s.suporteEnviarDesativado,
              )}
            >
              <MaterialIcons name="send" size={18} color="#fff" />
              <span className={s.suporteEnviarTexto}>{T("Enviar")}</span>
            </button>
          </div>
        </div>
      </BottomSheetModal>
    </div>
  );
}
